import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Save, Trash2, Plus, MapPin, Star, X, Sparkles } from "lucide-react";
import ImageField from "@/components/ImageField";

const LOC_STATUSES = [
  { v: "pending", label: "Pending" },
  { v: "approved", label: "Approved" },
  { v: "rejected", label: "Rejected" },
];

export default function AdminTruthMarkers() {
  return (
    <div data-testid="admin-truth-markers" className="space-y-10">
      <SeedTools />
      <PageEditor />
      <ProductsManager />
      <LocationsList />
      <SponsorshipsList />
    </div>
  );
}

function SeedTools() {
  const [busy, setBusy] = useState(false);
  const [aiCount, setAiCount] = useState(8);
  const [artCount, setArtCount] = useState(8);
  const seed = async (source, count) => {
    setBusy(true);
    try {
      const { data } = await http.post(`/admin/truth-markers/seed`, {}, { params: { source, count } });
      toast.success(`Created ${data.created} ${source === "ai" ? "AI-generated" : "artwork-sourced"} marker locations.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed failed.");
    } finally { setBusy(false); }
  };
  return (
    <section className="border border-[#C9A055]/30 bg-[#0a0a0a] p-6" data-testid="tm-seed-tools">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-2">Seed Markers</div>
      <div className="font-display text-xl text-[#F4F1EA] mb-1">Populate the marker gallery</div>
      <p className="font-body text-sm text-[#B5B0A7] mb-5 max-w-xl">
        Auto-create approved sample marker locations to show off the system before real submissions arrive.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="border border-white/10 p-4">
          <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-1">AI Generate</div>
          <div className="font-display text-[#F4F1EA] mb-2">Fresh truths + sample spots</div>
          <p className="font-body text-[12px] text-[#8a857d] mb-3">Uses the Universal LLM key. Metered per call.</p>
          <div className="flex gap-2 items-center">
            <input type="number" min="1" max="20" value={aiCount} onChange={(e) => setAiCount(Number(e.target.value) || 8)}
                   className="stc-input w-20 text-sm" data-testid="tm-seed-ai-count" />
            <button onClick={() => seed("ai", aiCount)} disabled={busy} data-testid="tm-seed-ai-btn" className="btn-gold text-[11px] flex items-center gap-1.5 disabled:opacity-60">
              <Sparkles size={11} /> Generate
            </button>
          </div>
        </div>
        <div className="border border-white/10 p-4">
          <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-1">From Artwork</div>
          <div className="font-display text-[#F4F1EA] mb-2">Pull from your collection</div>
          <p className="font-body text-[12px] text-[#8a857d] mb-3">Uses artwork meanings as marker truths. No LLM cost.</p>
          <div className="flex gap-2 items-center">
            <input type="number" min="1" max="20" value={artCount} onChange={(e) => setArtCount(Number(e.target.value) || 8)}
                   className="stc-input w-20 text-sm" data-testid="tm-seed-art-count" />
            <button onClick={() => seed("artwork", artCount)} disabled={busy} data-testid="tm-seed-art-btn" className="btn-outline text-[11px] flex items-center gap-1.5 disabled:opacity-60">
              Pull
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductsManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reload = () => {
    setLoading(true);
    http.get("/admin/marker-products")
      .then(({ data }) => setRows(data || []))
      .catch(() => toast.error("Couldn't load products."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const patch = async (id, body) => {
    try { await http.patch(`/admin/marker-products/${id}`, body); toast.success("Updated."); reload(); }
    catch { toast.error("Update failed."); }
  };
  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    try { await http.delete(`/admin/marker-products/${id}`); toast.success("Deleted."); reload(); }
    catch { toast.error("Delete failed."); }
  };
  const save = async (form) => {
    try {
      if (editing?.id) await http.patch(`/admin/marker-products/${editing.id}`, form);
      else await http.post(`/admin/marker-products`, form);
      toast.success("Saved.");
      setEditing(null);
      reload();
    } catch { toast.error("Save failed."); }
  };

  return (
    <section className="border border-white/10 bg-[#0a0a0a] p-6" data-testid="tm-products">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="font-display text-xl text-[#F4F1EA]">Marker Products ({rows.length})</div>
          <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mt-1">Sticker packs, kits, and custom marker products</div>
        </div>
        <button onClick={() => setEditing({})} data-testid="tm-product-new" className="btn-gold flex items-center gap-2">
          <Plus size={14} /> New Product
        </button>
      </div>
      {loading ? <div className="text-[#8a857d]">Loading…</div> : (
        <div className="space-y-2">
          {rows.map((p) => (
            <div key={p.id} className="border border-white/10 bg-[#050505] p-4 flex items-center gap-3" data-testid={`tm-product-${p.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display text-[#F4F1EA]">{p.name} <span className="font-ui text-[10px] text-[#8a857d] ml-2">{p.slug}</span></div>
                <div className="font-body text-[12px] text-[#8a857d] truncate">{p.description}</div>
              </div>
              <div className="font-display text-[#C9A055] w-20 text-right">${Number(p.price).toFixed(2)}</div>
              <button onClick={() => patch(p.id, { active: !p.active })} className={`btn-outline text-[10px] w-24 ${p.active ? "border-emerald-500 text-emerald-400" : ""}`}>
                {p.active ? "Active" : "Hidden"}
              </button>
              <button onClick={() => setEditing(p)} className="btn-outline text-[10px]">Edit</button>
              <button onClick={() => del(p.id)} className="btn-outline text-[10px] border-red-700 text-red-400"><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      )}
      {editing && <ProductEditor initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </section>
  );
}

function ProductEditor({ initial, onClose, onSave }) {
  const [f, setF] = useState({
    slug: initial.slug || "", name: initial.name || "", price: initial.price ?? 5,
    description: initial.description || "", image: initial.image || "",
    kind: initial.kind || "sticker", active: initial.active ?? true,
    shippable: initial.shippable ?? true, digital_qr: initial.digital_qr ?? false,
    qty_min: initial.qty_min || 1,
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = (e) => {
    e?.preventDefault?.();
    if (!f.slug.trim() || !f.name.trim() || !f.price) { toast.error("Slug, name, and price required."); return; }
    onSave({ ...f, price: Number(f.price), qty_min: Number(f.qty_min) });
  };
  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 max-w-lg w-full max-h-[92vh] overflow-y-auto" data-testid="tm-product-editor">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="font-display text-xl text-[#F4F1EA]">{initial.id ? "Edit Product" : "New Product"}</div>
          <button type="button" onClick={onClose} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Slug"><input className="stc-input" value={f.slug} onChange={(e) => set("slug", e.target.value)} disabled={!!initial.id} /></Field>
            <Field label="Price ($)"><input className="stc-input" type="number" min="0.01" step="0.01" value={f.price} onChange={(e) => set("price", e.target.value)} /></Field>
          </div>
          <Field label="Name"><input className="stc-input" value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Description"><textarea className="stc-input min-h-[80px]" value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
          <Field label="Image (optional)"><ImageField value={f.image} onChange={(v) => set("image", v)} testid="tm-product-image" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kind">
              <select className="stc-input" value={f.kind} onChange={(e) => set("kind", e.target.value)}>
                <option value="sticker">Sticker</option>
                <option value="custom">Custom</option>
                <option value="digital">Digital</option>
                <option value="kit">Kit</option>
              </select>
            </Field>
            <Field label="Min Qty"><input className="stc-input" type="number" min="1" value={f.qty_min} onChange={(e) => set("qty_min", e.target.value)} /></Field>
          </div>
          <div className="flex gap-4 pt-2 flex-wrap">
            <label className="flex items-center gap-2 font-body text-sm cursor-pointer">
              <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#C9A055]" /> Active
            </label>
            <label className="flex items-center gap-2 font-body text-sm cursor-pointer">
              <input type="checkbox" checked={f.shippable} onChange={(e) => set("shippable", e.target.checked)} className="accent-[#C9A055]" /> Shippable
            </label>
            <label className="flex items-center gap-2 font-body text-sm cursor-pointer">
              <input type="checkbox" checked={f.digital_qr} onChange={(e) => set("digital_qr", e.target.checked)} className="accent-[#C9A055]" /> Digital QR
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-white/10">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button type="submit" className="btn-gold" data-testid="tm-product-save">Save</button>
        </div>
      </form>
    </div>
  );
}

function PageEditor() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get("/admin/truth-markers/settings").then(({ data }) => setS(data)).catch(() => toast.error("Couldn't load.")); }, []);
  if (!s) return null;
  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setBusy(true);
    try { await http.patch("/admin/truth-markers/settings", s); toast.success("Saved."); }
    catch { toast.error("Save failed."); } finally { setBusy(false); }
  };

  const updateLevel = (i, key, val) => {
    const next = [...(s.levels || [])];
    next[i] = { ...next[i], [key]: key === "amount" ? Number(val) || 0 : val };
    set("levels", next);
  };
  const addLevel = () => set("levels", [...(s.levels || []), { slug: `level-${Date.now()}`, label: "$0", amount: 0, tagline: "" }]);
  const removeLevel = (i) => { const next = [...(s.levels || [])]; next.splice(i, 1); set("levels", next); };

  return (
    <section className="border border-white/10 bg-[#0a0a0a] p-6" data-testid="tm-page-editor">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="font-display text-xl text-[#F4F1EA]">Truth Markers — Page Content</div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 font-body text-sm text-[#F4F1EA] cursor-pointer" data-testid="tm-toggle-enabled">
            <input type="checkbox" checked={s.enabled !== false} onChange={(e) => set("enabled", e.target.checked)} className="accent-[#C9A055]" />
            Page enabled
          </label>
          <button onClick={save} disabled={busy} data-testid="tm-page-save" className="btn-gold flex items-center gap-2 disabled:opacity-60">
            <Save size={14} /> {busy ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>
      <Field label="Hero image"><ImageField value={s.hero_image || ""} onChange={(v) => set("hero_image", v)} testid="tm-hero" /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Title"><input className="stc-input" value={s.title} onChange={(e) => set("title", e.target.value)} maxLength={120} /></Field>
        <Field label="Subtitle"><input className="stc-input" value={s.subtitle} onChange={(e) => set("subtitle", e.target.value)} maxLength={200} /></Field>
      </div>
      <Field label="Intro"><textarea className="stc-input min-h-[80px]" value={s.intro} onChange={(e) => set("intro", e.target.value)} maxLength={1200} /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="What heading"><input className="stc-input" value={s.what_heading} onChange={(e) => set("what_heading", e.target.value)} maxLength={120} /></Field>
        <Field label="How heading"><input className="stc-input" value={s.how_heading} onChange={(e) => set("how_heading", e.target.value)} maxLength={120} /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="What body"><textarea className="stc-input min-h-[80px]" value={s.what_body} onChange={(e) => set("what_body", e.target.value)} maxLength={1200} /></Field>
        <Field label="How body"><textarea className="stc-input min-h-[80px]" value={s.how_body} onChange={(e) => set("how_body", e.target.value)} maxLength={1200} /></Field>
      </div>
      <Field label="Sponsor heading"><input className="stc-input" value={s.sponsor_heading} onChange={(e) => set("sponsor_heading", e.target.value)} maxLength={120} /></Field>
      <Field label="Sponsor body"><textarea className="stc-input min-h-[60px]" value={s.sponsor_body} onChange={(e) => set("sponsor_body", e.target.value)} maxLength={1200} /></Field>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Sponsor CTA"><input className="stc-input" value={s.sponsor_cta} onChange={(e) => set("sponsor_cta", e.target.value)} maxLength={60} /></Field>
        <Field label="Stickers CTA"><input className="stc-input" value={s.stickers_cta} onChange={(e) => set("stickers_cta", e.target.value)} maxLength={60} /></Field>
        <Field label="Stickers URL"><input className="stc-input" value={s.stickers_url} onChange={(e) => set("stickers_url", e.target.value)} maxLength={300} /></Field>
      </div>
      <Field label="Placement guidelines"><textarea className="stc-input min-h-[110px]" value={s.guidelines} onChange={(e) => set("guidelines", e.target.value)} maxLength={2000} /></Field>

      {/* Sponsorship levels */}
      <div className="mt-5 border-t border-white/10 pt-5">
        <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-3">Sponsorship Levels</div>
        <div className="space-y-2">
          {(s.levels || []).map((lv, i) => (
            <div key={i} className="grid grid-cols-[80px_80px_1fr_auto] gap-2 items-center" data-testid={`tm-level-row-${i}`}>
              <input className="stc-input text-sm" placeholder="$10" value={lv.label} onChange={(e) => updateLevel(i, "label", e.target.value)} />
              <input className="stc-input text-sm" placeholder="10" type="number" min="0" value={lv.amount} onChange={(e) => updateLevel(i, "amount", e.target.value)} />
              <input className="stc-input text-sm" placeholder="Tagline" value={lv.tagline} onChange={(e) => updateLevel(i, "tagline", e.target.value)} />
              <button onClick={() => removeLevel(i)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <button onClick={addLevel} data-testid="tm-add-level" className="btn-outline mt-2 flex items-center gap-1.5 text-[11px]"><Plus size={11} /> Add Level</button>
      </div>
    </section>
  );
}

function LocationsList() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const reload = () => {
    setLoading(true);
    http.get("/admin/truth-markers/locations", { params: filter === "all" ? {} : { status: filter } })
      .then(({ data }) => setRows(data || []))
      .catch(() => toast.error("Load failed."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  const patch = async (id, body) => {
    try { await http.patch(`/admin/truth-markers/locations/${id}`, body); toast.success("Updated."); reload(); }
    catch { toast.error("Update failed."); }
  };
  const del = async (id) => {
    if (!confirm("Delete this location?")) return;
    try { await http.delete(`/admin/truth-markers/locations/${id}`); toast.success("Deleted."); reload(); }
    catch { toast.error("Delete failed."); }
  };
  const reprint = async (m) => {
    try {
      const { data } = await http.post(`/admin/truth-markers/locations/${m.id}/reprint-sticker`);
      window.open(data.sticker_url, "_blank", "noopener,noreferrer");
      toast.success(`Reprinting ${data.tm_id}`);
      reload();
    } catch { toast.error("Reprint failed."); }
  };
  const create = async (form) => {
    try {
      await http.post("/admin/truth-markers/locations", form);
      toast.success("Location created.");
      setCreating(false);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed."); }
  };

  return (
    <section className="border border-white/10 bg-[#0a0a0a] p-6" data-testid="tm-locations">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="font-display text-xl text-[#F4F1EA]">Marker Locations ({rows.length})</div>
        <div className="flex flex-wrap gap-1.5 items-center">
          {["pending", "approved", "rejected", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`tm-filter-${f}`}
                    className={`font-ui text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 border ${filter === f ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/15 text-[#8a857d] hover:text-[#F4F1EA]"}`}>
              {f}
            </button>
          ))}
          <button onClick={() => setCreating(true)} data-testid="tm-loc-create" className="btn-gold ml-2 text-[11px] flex items-center gap-1.5">
            <Plus size={11} /> New Location
          </button>
        </div>
      </div>
      {loading ? <div className="text-[#8a857d] font-body">Loading…</div> : rows.length === 0 ? (
        <div className="text-center border border-dashed border-white/10 p-10 text-[#8a857d] font-body">No locations in this view.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((m) => (
            <div key={m.id} className="border border-white/10 bg-[#050505] p-4 flex gap-3" data-testid={`tm-row-${m.id}`}>
              {m.photo_url && <img src={m.photo_url} alt="" className="w-20 h-20 object-cover border border-white/10" />}
              <div className="flex-1 min-w-0">
                <div className="font-display text-[15px] text-[#F4F1EA]">&ldquo;{m.truth}&rdquo;</div>
                <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mt-1 flex items-center gap-1">
                  <MapPin size={11} /> {m.location_name}{m.address ? ` · ${m.address}` : ""}
                </div>
                {m.story && <p className="font-body italic text-[12px] text-[#8a857d] mt-2 line-clamp-2">{m.story}</p>}
                <div className="font-ui text-[10px] text-[#8a857d] mt-1.5 flex flex-wrap gap-x-3">
                  <span>{m.submitted_by || "Anonymous"}{m.email ? ` · ${m.email}` : ""}</span>
                  {m.tm_id && <span className="text-[#C9A055]">ID: {m.tm_id}</span>}
                  {m.tm_id && <span>· Scans: {m.scans || 0} · Needed: {m.needed_count || 0}</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <select value={m.status} onChange={(e) => patch(m.id, { status: e.target.value })} data-testid={`tm-status-${m.id}`}
                        className="stc-input text-[11px] py-1 w-[110px]">
                  {LOC_STATUSES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
                {m.tm_id && (
                  <a href={`/m/${m.tm_id}`} target="_blank" rel="noopener noreferrer"
                     data-testid={`tm-view-${m.id}`}
                     className="btn-outline text-[10px] flex items-center justify-center gap-1">
                    View page
                  </a>
                )}
                <button onClick={() => reprint(m)} data-testid={`tm-reprint-${m.id}`}
                        className="btn-outline text-[10px] border-[#C9A055] text-[#C9A055] flex items-center justify-center gap-1">
                  <Sparkles size={11} /> Reprint
                </button>
                <button onClick={() => patch(m.id, { featured: !m.featured })} title="Feature"
                        className={`btn-outline text-[10px] flex items-center justify-center gap-1 ${m.featured ? "border-[#C9A055] text-[#C9A055]" : ""}`}>
                  <Star size={11} /> {m.featured ? "Unfeature" : "Feature"}
                </button>
                <button onClick={() => del(m.id)} className="btn-outline text-[10px] border-red-700 text-red-400 flex items-center justify-center gap-1">
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {creating && <LocationCreator onClose={() => setCreating(false)} onSave={create} />}
    </section>
  );
}

function LocationCreator({ onClose, onSave }) {
  const [f, setF] = useState({
    truth: "", location_name: "", address: "", photo_url: "", story: "",
    lat: "", lng: "", status: "approved", featured: false, admin_note: "",
  });
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Image only."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/truth-markers/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_url", (process.env.REACT_APP_BACKEND_URL || "") + data.url);
      toast.success("Uploaded.");
    } catch { toast.error("Upload failed."); }
    finally { setUploading(false); }
  };

  const submit = (e) => {
    e?.preventDefault?.();
    if (!f.truth.trim() || !f.location_name.trim()) { toast.error("Truth and location required."); return; }
    const payload = Object.fromEntries(
      Object.entries(f).map(([k, v]) => [k, typeof v === "string" && v.trim() === "" ? null : v])
    );
    payload.lat = f.lat ? Number(f.lat) : null;
    payload.lng = f.lng ? Number(f.lng) : null;
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 max-w-xl w-full max-h-[92vh] overflow-y-auto" data-testid="tm-loc-creator">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="font-display text-xl text-[#F4F1EA]">New Marker Location</div>
          <button type="button" onClick={onClose} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Truth"><textarea className="stc-input min-h-[70px]" maxLength={240} value={f.truth} onChange={(e) => set("truth", e.target.value)} data-testid="tm-creator-truth" /></Field>
          <Field label="Location name"><input className="stc-input" maxLength={120} value={f.location_name} onChange={(e) => set("location_name", e.target.value)} data-testid="tm-creator-name" /></Field>
          <Field label="Address (optional)"><input className="stc-input" maxLength={240} value={f.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="Photo (optional)">
            <div className="flex items-center gap-3">
              <label className="btn-outline cursor-pointer text-[11px] inline-flex items-center">
                <input type="file" accept="image/*" onChange={upload} className="hidden" />
                {uploading ? "Uploading…" : (f.photo_url ? "Replace photo" : "Upload photo")}
              </label>
              {f.photo_url && <img src={f.photo_url} alt="" className="w-12 h-12 object-cover border border-white/10" />}
            </div>
          </Field>
          <Field label="Story (optional)"><textarea className="stc-input min-h-[80px]" maxLength={2000} value={f.story} onChange={(e) => set("story", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude (optional)"><input className="stc-input" type="number" step="any" value={f.lat} onChange={(e) => set("lat", e.target.value)} /></Field>
            <Field label="Longitude (optional)"><input className="stc-input" type="number" step="any" value={f.lng} onChange={(e) => set("lng", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <select className="stc-input" value={f.status} onChange={(e) => set("status", e.target.value)}>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </Field>
            <Field label="Featured">
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#C9A055]" /> Yes
              </label>
            </Field>
          </div>
          <Field label="Admin note (private)"><textarea className="stc-input min-h-[50px]" maxLength={600} value={f.admin_note} onChange={(e) => set("admin_note", e.target.value)} /></Field>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-white/10">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button type="submit" className="btn-gold" data-testid="tm-creator-save">Create Location</button>
        </div>
      </form>
    </div>
  );
}

function SponsorshipsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    http.get("/admin/truth-markers/sponsorships")
      .then(({ data }) => setRows(data || []))
      .catch(() => toast.error("Load failed."))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return null;
  return (
    <section className="border border-white/10 bg-[#0a0a0a] p-6" data-testid="tm-sponsorships">
      <div className="font-display text-xl text-[#F4F1EA] mb-1">Marker Sponsorships ({rows.length})</div>
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-4">Most recent first · includes pending Stripe sessions</div>
      {rows.length === 0 ? (
        <div className="border border-dashed border-white/10 p-8 text-center text-[#8a857d] font-body">No sponsorships yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-body">
            <thead className="text-left font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] border-b border-white/10">
              <tr>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Level</th>
                <th className="py-2 pr-3 text-right">Amount</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="border-b border-white/5" data-testid={`tm-sponsor-${s.id}`}>
                  <td className="py-2 pr-3 text-[#F4F1EA]">{s.anonymous ? "Anonymous" : s.name}<div className="font-ui text-[10px] text-[#8a857d]">{s.email}</div></td>
                  <td className="py-2 pr-3 text-[#C9A055]">{s.level_label}</td>
                  <td className="py-2 pr-3 text-right text-[#F4F1EA]">${s.amount?.toFixed(2)}</td>
                  <td className="py-2 pr-3"><span className={s.status === "paid" ? "text-emerald-400" : "text-[#8a857d]"}>{s.status}</span></td>
                  <td className="py-2 pr-3 text-[#8a857d] font-ui text-[11px]">{s.paid_at ? new Date(s.paid_at).toLocaleDateString() : new Date(s.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
